import React, { useContext, useRef, useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import cn from 'classnames';
import { useLocation, withRouter } from 'react-router-dom';

import { UserContext } from 'context/UserContext';
import { InterviewContext } from 'context/Interview/InterviewContext';

import { isAdmin } from 'constants/UserRoles';

import Footer from 'components/Page/Footer/Footer';

import Routes, { NavLinksOrder, getNavCategory } from 'constants/Routes';
import style from './Page.module.scss';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import HeaderMessage from './HeaderAuthorized/HeaderMessage/HeaderMessage';
import { BrowserContext } from 'context/OnlineInterview/BrowserContext';
import Helmet from 'react-helmet';
import Welcome from 'components/Welcome/Welcome';
import Modal from 'components/common/Dialog/Modal';
import AdditionalFieldsForm from 'components/Profile/AdditionalFieldsForm/AdditionalFieldsForm';
import { chatWootHelper } from '../../helpers/chatWootHelper';
import useResolutionChange from 'hooks/common/useResolutionChange';

const OnlineInterviewRoutes = [
    Routes.interviewOnline.path,
    Routes.observer.path,
    Routes.waitingRoom.path,
    Routes.instanceWaitingRoom.path,
];

const UnmountDirections = {
    left: 'left',
    right: 'right',
    bottom: 'bottom',
};

const UnmountStyles = {
    left: { [style.comingFromLeft]: true },
    right: { [style.comingFromRight]: true },
    bottom: { [style.comingFromBottom]: true },
};

export const PageStyles = {
    profilePage: {
        backgroundImage: 'url(/profileBackground.svg)',
        backgroundSize: 'contain',
        backgroundPosition: '0% 0%',
    },
    profileInitial: {
        backgroundImage: 'url(/profileBackground.svg)',
        backgroundSize: 'contain',
        backgroundPosition: '0 -100%',
    },
    leftOverRight: {
        backgroundImage: 'url(/leftCornerLayer2.svg),url(/leftCornerLayer1.svg), url(/rightCorner.svg)',
        backgroundPosition: '0% 0%, 0% 0%, 100% -100%',
    },
    rightOverLeft: {
        backgroundImage: 'url(/leftCornerLayer1.svg), url(/leftCornerLayer2.svg), url(/rightCorner.svg)',
        backgroundPosition: '-100% -100%, -100% -100%, 100% 0%',
    },
    registerInitial: {
        backgroundImage: 'url(/leftCornerLayer1.svg), url(/leftCornerLayer2.svg), url(/rightCorner.svg)',
        backgroundPosition: '-100% -100%, -100% -100%, 100% -100%',
    },
    onlineInterview: {
        backgroundImage: 'none',
        paddingTop: '0rem',
    },
};

function Page({ children, browsePage, projectInfo, match, noFooter, contentWrapperClassName }) {
    const {
        user,
        submissionGotIt,
        setPageScrollbarRef,
        previousRoute,
        scrollBarsEventHandler,
        gotIt,
        showWelcomeModal,
        showAdditionalModal,
    } = useContext(UserContext);
    const location = useLocation();
    useResolutionChange();
    const { interviewsPageScroll } = useContext(InterviewContext);
    const { browser } = useContext(BrowserContext);
    const [direction, setDirection] = useState(resolveDirection(previousRoute.navCategory, getNavCategory(match.path)));
    const active = user && user.active;
    const showHeaderMessage = user && match.path !== Routes.welcome.path && !active && !submissionGotIt;
    const scrollRef = useRef(null);

    const { styleToMount, styleToUnmount } = resolvePageStyles(match, previousRoute);
    const [bgStyle, setBgStyle] = useState(styleToUnmount);

    const chatWoodToggle = () => {
        if (chatWootHelper.isBlocked()) {
            chatWootHelper.hide();
        } else {
            chatWootHelper.show();
        }
    };

    useEffect(() => {
        setPageScrollbarRef(scrollRef);
        setTimeout(() => {
            setBgStyle(styleToMount);
            setDirection('');
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        chatWoodToggle();
    }, [location]);

    const className = cn(style.page, {
        [style.browsePage]: browsePage,
        [style.projectInfo]: projectInfo,
        [style.unmounted]: direction,
        [style.noMargin]: true,
        ...UnmountStyles[direction],
    });

    const isOnlineInterview = OnlineInterviewRoutes.includes(match.path);
    return (
        <>
            {isOnlineInterview && (
                <Helmet>
                    <script>
                        {`
                            if(!!UST) UST.sendData = () => {};
                        `}
                    </script>
                </Helmet>
            )}
            <div className={cn(style.pageContainer, showHeaderMessage && style.pageContainerTop)} style={bgStyle || {}}>
                <Scrollbars
                    height='100%'
                    className={cn(style.scrollbar, { [style.simpleScroll]: interviewsPageScroll > 0 })}
                    ref={scrollRef}
                    renderView={(props) => <div {...props} className={className} />}
                    onScrollFrame={scrollBarsEventHandler}
                >
                    <ErrorBoundary>
                        {showHeaderMessage && <HeaderMessage onGotIt={gotIt} />}
                        <div className={cn(style.contentWrapper, contentWrapperClassName)}>{children}</div>
                    </ErrorBoundary>
                    {((user && !isAdmin(user.role)) || !user) &&
                        !isOnlineInterview &&
                        !noFooter &&
                        !browser.isMobile && <Footer />}
                </Scrollbars>
                {showWelcomeModal && (
                    <Modal>
                        <Scrollbars height='100%' autoHeight autoHeightMax='95vh'>
                            <Welcome isModal />
                        </Scrollbars>
                    </Modal>
                )}
                {showAdditionalModal && (
                    <Modal>
                        <Scrollbars height='100%' autoHeight autoHeightMax='95vh'>
                            <AdditionalFieldsForm />
                        </Scrollbars>
                    </Modal>
                )}
            </div>
        </>
    );
}

const resolveDirection = (prevRouteCategory, currentRouteCategory) => {
    if (NavLinksOrder[prevRouteCategory] < NavLinksOrder[currentRouteCategory]) {
        return UnmountDirections.left;
    }

    if (NavLinksOrder[prevRouteCategory] > NavLinksOrder[currentRouteCategory]) {
        return UnmountDirections.right;
    }

    return UnmountDirections.bottom;
};

export const registerRoutes = [
    Routes.signUp.path,
    Routes.signIn.path,
    `${Routes.signUp.path}/:role`,
    Routes.resetPassword.path,
];
export const leftOverRightRoutes = [
    Routes.signIn.path,
    `${Routes.signUp.path}/:role`,
    Routes.welcome.path,
    Routes.confirmRegistration.path,
];
export const rightOverLeftRoutes = [Routes.signUp.path, Routes.resetPassword.path];
const onlineInterviewPaths = [
    Routes.interviewOnline.path,
    Routes.waitingRoom.path,
    Routes.moderatorWaitingRoom.path,
    Routes.instanceWaitingRoom.path,
];
const resolvePageStyles = (match, prevPath) => {
    if (onlineInterviewPaths.includes(match.path)) {
        return { styleToMount: PageStyles.onlineInterview, styleToUnmount: {} };
    }

    if (!prevPath.route) {
        return getInitialStylesForPage(match.path);
    }
    if (`${Routes.signUpTeam.path}/:token` === match.path) {
        return { styleToMount: PageStyles.leftOverRight, styleToUnmount: PageStyles.rightOverLeft };
    }
    if (rightOverLeftRoutes.includes(match.path) && leftOverRightRoutes.includes(prevPath.route)) {
        return { styleToMount: PageStyles.rightOverLeft, styleToUnmount: PageStyles.leftOverRight };
    }

    if (rightOverLeftRoutes.includes(prevPath.route) && leftOverRightRoutes.includes(match.path)) {
        return { styleToMount: PageStyles.leftOverRight, styleToUnmount: PageStyles.rightOverLeft };
    }

    if (match.path === Routes.confirmRegistration.path) {
        return { styleToMount: PageStyles.leftOverRight, styleToUnmount: PageStyles.leftOverRight };
    }

    if (prevPath.route === `${Routes.signUp.path}/:role` && match.path === Routes.signIn.path) {
        return { styleToMount: PageStyles.leftOverRight, styleToUnmount: PageStyles.leftOverRight };
    }

    if (match.path === Routes.welcome.path) {
        return { styleToMount: PageStyles.leftOverRight, styleToUnmount: PageStyles.leftOverRight };
    }

    if (prevPath.route === Routes.signIn.path) {
        return { styleToMount: PageStyles.profilePage, styleToUnmount: PageStyles.profileInitial };
    }

    if (prevPath.route === Routes.welcome.path && match.path === Routes.signIn.path) {
        return { styleToMount: PageStyles.leftOverRight, styleToUnmount: PageStyles.leftOverRight };
    }

    if (!registerRoutes.includes(prevPath.route) && registerRoutes.includes(match.path)) {
        return getInitialStylesForPage(match.path);
    }

    return { styleToUnmount: PageStyles.profilePage, styleToMount: PageStyles.profilePage };
};

const getInitialStylesForPage = (path) => {
    switch (path) {
        case Routes.signIn.path:
        case `${Routes.signUp.path}/:role`:
        case Routes.confirmRegistration.path:
        case Routes.welcome.path:
            return { styleToMount: PageStyles.leftOverRight, styleToUnmount: PageStyles.registerInitial };
        case Routes.signUp.path:
        case Routes.resetPassword.path:
            return { styleToMount: PageStyles.rightOverLeft, styleToUnmount: PageStyles.registerInitial };
        default:
            return { styleToMount: PageStyles.profilePage, styleToUnmount: PageStyles.profileInitial };
    }
};

export default withRouter(Page);
